/**
 * ProcessFlowDiagram Component
 *
 * Visual representation of a process flow with entry criteria,
 * decision branches, and actions. Provides a clear overview
 * of how the process will execute.
 *
 * @component
 */

import React, { useState, useMemo } from 'react';
import {
  Play,
  GitBranch,
  ArrowDown,
  CheckCircle,
  Database,
  Mail,
  FileText,
  Clock,
  Users,
  Send,
  Zap,
  Workflow,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Filter
} from 'lucide-react';
import { StandardButton } from '../../standard';
import type {
  Process,
  ProcessCriteriaGroup,
  ProcessAction,
  ScheduledAction,
  ActionType
} from '../../../types/processBuilder';

// ============================================
// INLINE BADGE COMPONENT
// ============================================

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'success' | 'warning' | 'danger' | 'info' | 'secondary';
  size?: 'sm' | 'md';
}

const Badge: React.FC<BadgeProps> = ({ children, variant = 'secondary', size = 'md' }) => {
  const variantClasses = {
    success: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    warning: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
    danger: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    info: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
    secondary: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
  };

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm'
  };

  return (
    <span className={`inline-flex items-center font-medium rounded-full ${variantClasses[variant]} ${sizeClasses[size]}`}>
      {children}
    </span>
  );
};

// ============================================
// INTERFACES
// ============================================

interface ProcessFlowDiagramProps {
  process: Process;
  compact?: boolean;
  showLabels?: boolean;
  onNodeClick?: (nodeType: string, nodeId: string) => void;
}

// ============================================
// CONSTANTS
// ============================================

const ACTION_ICONS: Record<ActionType, React.ReactNode> = {
  field_update: <Database size={16} />,
  create_record: <FileText size={16} />,
  update_related: <Database size={16} />,
  send_email: <Mail size={16} />,
  post_to_feed: <Send size={16} />,
  create_task: <FileText size={16} />,
  submit_for_approval: <Users size={16} />,
  call_apex: <Zap size={16} />,
  invoke_process: <Workflow size={16} />
};

const ACTION_COLORS: Record<ActionType, { bg: string; border: string; text: string }> = {
  field_update: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-blue-700 dark:text-blue-300'
  },
  create_record: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    text: 'text-green-700 dark:text-green-300'
  },
  update_related: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-blue-700 dark:text-blue-300'
  },
  send_email: {
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    border: 'border-purple-200 dark:border-purple-800',
    text: 'text-purple-700 dark:text-purple-300'
  },
  post_to_feed: {
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    border: 'border-orange-200 dark:border-orange-800',
    text: 'text-orange-700 dark:text-orange-300'
  },
  create_task: {
    bg: 'bg-teal-50 dark:bg-teal-900/20',
    border: 'border-teal-200 dark:border-teal-800',
    text: 'text-teal-700 dark:text-teal-300'
  },
  submit_for_approval: {
    bg: 'bg-indigo-50 dark:bg-indigo-900/20',
    border: 'border-indigo-200 dark:border-indigo-800',
    text: 'text-indigo-700 dark:text-indigo-300'
  },
  call_apex: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    border: 'border-yellow-200 dark:border-yellow-800',
    text: 'text-yellow-700 dark:text-yellow-300'
  },
  invoke_process: {
    bg: 'bg-pink-50 dark:bg-pink-900/20',
    border: 'border-pink-200 dark:border-pink-800',
    text: 'text-pink-700 dark:text-pink-300'
  }
};

const TRIGGER_LABELS: Record<string, string> = {
  record_created: 'Record Created',
  record_updated: 'Record Updated',
  record_created_or_updated: 'Record Created/Updated',
  platform_event: 'Platform Event',
  invocable: 'Invoked'
};

// ============================================
// CONNECTOR COMPONENT
// ============================================

interface ConnectorProps {
  label?: string;
  dashed?: boolean;
  color?: 'gray' | 'green' | 'blue' | 'purple';
}

const Connector: React.FC<ConnectorProps> = ({ label, dashed = false, color = 'gray' }) => {
  const colorClasses = {
    gray: 'border-gray-300 dark:border-gray-600',
    green: 'border-green-400 dark:border-green-600',
    blue: 'border-blue-400 dark:border-blue-600',
    purple: 'border-purple-400 dark:border-purple-600'
  };

  return (
    <div className="flex flex-col items-center py-2">
      <div
        className={`w-0 h-6 border-l-2 ${colorClasses[color]} ${dashed ? 'border-dashed' : ''}`}
      />
      {label && (
        <span className="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-900 px-2 -my-2 relative z-10">
          {label}
        </span>
      )}
      <ArrowDown size={16} className={`${colorClasses[color].replace('border', 'text')} -mt-1`} />
    </div>
  );
};

// ============================================
// TRIGGER NODE COMPONENT
// ============================================

interface TriggerNodeProps {
  process: Process;
  onClick?: () => void;
}

const TriggerNode: React.FC<TriggerNodeProps> = ({ process, onClick }) => (
  <div
    onClick={onClick}
    className="relative bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg p-4 shadow-md cursor-pointer hover:shadow-lg transition-shadow min-w-[200px]"
  >
    <div className="flex items-center gap-3">
      <div className="p-2 bg-white/20 rounded-lg">
        <Play size={20} />
      </div>
      <div>
        <p className="font-semibold">Start Process</p>
        <p className="text-xs text-green-100">
          {TRIGGER_LABELS[process.triggerType] || process.triggerType}
        </p>
      </div>
    </div>
    <div className="mt-2 pt-2 border-t border-green-400/30">
      <p className="text-xs text-green-100">Object: {process.objectType}</p>
    </div>
  </div>
);

// ============================================
// CRITERIA NODE COMPONENT
// ============================================

interface CriteriaNodeProps {
  criteria: ProcessCriteriaGroup | undefined;
  label?: string;
  onClick?: () => void;
}

const CriteriaNode: React.FC<CriteriaNodeProps> = ({ criteria, label = 'Entry Criteria', onClick }) => {
  const conditionCount = criteria?.conditions?.length || 0;

  return (
    <div
      onClick={onClick}
      className="relative bg-white dark:bg-gray-800 border-2 border-amber-400 dark:border-amber-500 rounded-lg p-4 shadow-md cursor-pointer hover:shadow-lg transition-shadow min-w-[200px]"
    >
      <div className="flex items-center gap-3">
        <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg text-amber-600 dark:text-amber-400">
          <Filter size={20} />
        </div>
        <div>
          <p className="font-semibold text-gray-900 dark:text-gray-100">{label}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {conditionCount === 0
              ? 'Always runs'
              : `${conditionCount} condition${conditionCount !== 1 ? 's' : ''}`}
          </p>
        </div>
      </div>
      {criteria && criteria.conditions && criteria.conditions.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Logic: {criteria.conditionLogic}
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================
// DECISION NODE COMPONENT
// ============================================

interface DecisionNodeProps {
  group: ProcessCriteriaGroup;
  index: number;
  immediateActions: ProcessAction[];
  scheduledActions: ScheduledAction[];
  expanded?: boolean;
  onToggle?: () => void;
  onClick?: () => void;
}

const DecisionNode: React.FC<DecisionNodeProps> = ({
  group,
  index,
  immediateActions,
  scheduledActions,
  expanded = false,
  onToggle,
  onClick
}) => {
  const totalActions = immediateActions.length + scheduledActions.reduce((sum, sa) => sum + sa.actions.length, 0);

  return (
    <div className="relative">
      {/* Diamond Decision Shape */}
      <div
        onClick={onClick}
        className="bg-white dark:bg-gray-800 border-2 border-blue-400 dark:border-blue-500 rounded-lg p-4 shadow-md cursor-pointer hover:shadow-lg transition-shadow min-w-[250px]"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
              <GitBranch size={20} />
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">
                {group.name || `Decision ${index + 1}`}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {group.conditions?.length || 0} conditions
              </p>
            </div>
          </div>
          {totalActions > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggle?.();
              }}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          )}
        </div>

        {/* Summary badges */}
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <Badge variant="info" size="sm">
            {immediateActions.length} actions
          </Badge>
          {scheduledActions.length > 0 && (
            <Badge variant="secondary" size="sm">
              {scheduledActions.length} scheduled
            </Badge>
          )}
        </div>
      </div>

      {/* Expanded Actions */}
      {expanded && (
        <div className="mt-2 ml-8 space-y-2">
          {/* Immediate Actions */}
          {immediateActions.map((action, actionIndex) => (
            <ActionNode key={action.id || actionIndex} action={action} />
          ))}

          {/* Scheduled Actions */}
          {scheduledActions.map((sa, saIndex) => (
            <ScheduledActionNode key={sa.id || saIndex} scheduledAction={sa} />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// ACTION NODE COMPONENT
// ============================================

interface ActionNodeProps {
  action: ProcessAction;
  onClick?: () => void;
}

const ActionNode: React.FC<ActionNodeProps> = ({ action, onClick }) => {
  const colors = ACTION_COLORS[action.type];

  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 p-3 rounded-lg border ${colors.bg} ${colors.border} cursor-pointer hover:shadow-md transition-shadow`}
    >
      <div className={colors.text}>
        {ACTION_ICONS[action.type]}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-medium text-sm ${colors.text}`}>
          {action.name}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {action.type.replace(/_/g, ' ')}
        </p>
      </div>
      {action.isActive === false && (
        <Badge variant="secondary" size="sm">Disabled</Badge>
      )}
    </div>
  );
};

// ============================================
// SCHEDULED ACTION NODE COMPONENT
// ============================================

interface ScheduledActionNodeProps {
  scheduledAction: ScheduledAction;
  onClick?: () => void;
}

const ScheduledActionNode: React.FC<ScheduledActionNodeProps> = ({ scheduledAction, onClick }) => {
  const formatSchedule = (): string => {
    const unit = scheduledAction.scheduleType.includes('days') ? 'days' :
                 scheduledAction.scheduleType.includes('hours') ? 'hours' : 'min';
    const direction = scheduledAction.scheduleType.includes('before') ? 'before' : 'after';
    return `${scheduledAction.scheduleValue}${unit} ${direction}`;
  };

  return (
    <div
      onClick={onClick}
      className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="flex items-center gap-2 mb-2">
        <Clock size={14} className="text-purple-600 dark:text-purple-400" />
        <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
          {scheduledAction.name}
        </span>
        <Badge variant="secondary" size="sm">{formatSchedule()}</Badge>
      </div>
      <div className="space-y-1 ml-5">
        {scheduledAction.actions.slice(0, 2).map((action, index) => (
          <div key={action.id || index} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
            {ACTION_ICONS[action.type]}
            <span>{action.name}</span>
          </div>
        ))}
        {scheduledAction.actions.length > 2 && (
          <p className="text-xs text-gray-500 dark:text-gray-500">
            +{scheduledAction.actions.length - 2} more actions
          </p>
        )}
      </div>
    </div>
  );
};

// ============================================
// END NODE COMPONENT
// ============================================

const EndNode: React.FC = () => (
  <div className="bg-gray-500 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-md">
    <CheckCircle size={24} />
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================

export const ProcessFlowDiagram: React.FC<ProcessFlowDiagramProps> = ({
  process,
  compact = false,
  showLabels = true,
  onNodeClick
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Toggle group expansion
  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Expand all groups
  const expandAll = () => {
    const allIds = new Set(process.criteriaGroups?.map(g => g.id) || []);
    setExpandedGroups(allIds);
  };

  // Collapse all groups
  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  // Calculate total actions
  const totalStats = useMemo(() => {
    let immediateCount = 0;
    let scheduledCount = 0;

    Object.values(process.immediateActions || {}).forEach(actions => {
      immediateCount += actions.length;
    });

    Object.values(process.scheduledActions || {}).forEach(scheduled => {
      scheduled.forEach(sa => {
        scheduledCount += sa.actions.length;
      });
    });

    return {
      groups: process.criteriaGroups?.length || 0,
      immediate: immediateCount,
      scheduled: scheduledCount
    };
  }, [process]);

  if (!process) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <AlertCircle size={48} className="mx-auto mb-3 opacity-50" />
        <p>No process data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      {!compact && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <span>{totalStats.groups} decision groups</span>
              <span>•</span>
              <span>{totalStats.immediate} immediate actions</span>
              <span>•</span>
              <span>{totalStats.scheduled} scheduled actions</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StandardButton size="sm" variant="ghost" onClick={expandAll}>
              Expand All
            </StandardButton>
            <StandardButton size="sm" variant="ghost" onClick={collapseAll}>
              Collapse All
            </StandardButton>
          </div>
        </div>
      )}

      {/* Flow Diagram */}
      <div className="flex flex-col items-center py-6 px-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 overflow-auto">
        {/* Trigger Node */}
        <TriggerNode
          process={process}
          onClick={() => onNodeClick?.('trigger', process.id)}
        />

        <Connector color="green" />

        {/* Entry Criteria */}
        <CriteriaNode
          criteria={process.entryCriteria}
          label="Entry Criteria"
          onClick={() => onNodeClick?.('entryCriteria', 'entry')}
        />

        {/* Decision Groups */}
        {process.criteriaGroups && process.criteriaGroups.length > 0 ? (
          <>
            {process.criteriaGroups.map((group, index) => (
              <React.Fragment key={group.id || index}>
                <Connector
                  color="blue"
                  label={showLabels ? `If ${group.name || `Decision ${index + 1}`}` : undefined}
                />

                <DecisionNode
                  group={group}
                  index={index}
                  immediateActions={process.immediateActions?.[group.id] || []}
                  scheduledActions={process.scheduledActions?.[group.id] || []}
                  expanded={expandedGroups.has(group.id)}
                  onToggle={() => toggleGroup(group.id)}
                  onClick={() => onNodeClick?.('decision', group.id)}
                />

                {/* Show if stop processing */}
                {group.stopProcessing && (
                  <div className="mt-2 px-3 py-1 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs rounded-full">
                    Stop if matched
                  </div>
                )}
              </React.Fragment>
            ))}

            <Connector color="gray" label="Complete" />
          </>
        ) : (
          <>
            <Connector color="gray" />
            <div className="text-center py-4 px-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
              <GitBranch size={24} className="mx-auto text-gray-400 dark:text-gray-500 mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No decision groups
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Add decision groups to define process logic
              </p>
            </div>
            <Connector color="gray" />
          </>
        )}

        {/* End Node */}
        <EndNode />
      </div>

      {/* Legend */}
      {!compact && (
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-green-500" />
            <span>Trigger</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-amber-400" />
            <span>Criteria</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-blue-400" />
            <span>Decision</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-purple-400" />
            <span>Scheduled</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-gray-500" />
            <span>End</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessFlowDiagram;
